import * as Sentry from '@sentry/react';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { Stripe, StripeError, StripePaymentElementChangeEvent } from '@stripe/stripe-js';
import cx from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import Button from '@/components/Button';
import CircularProgress from '@/components/CircularProgress';
import { type StripeLoadFailureKind, type StripeLoadSource, getStripe } from '@/core/stripe';
import { useThemeMode } from '@/hooks/useThemeMode';

import { StripeFormShell } from './StripeFormShell';
import { buildStripeAppearance, useStyles } from './styles';
import { StripePaymentMethodFormProps } from './types';

type StripeInstanceState =
  | { status: 'loading' }
  | { status: 'retrying' }
  | { status: 'ready'; stripe: Stripe }
  | { status: 'failed'; kind: StripeLoadFailureKind };

// getStripe() drops its memo only after a 'blocked' failure, so bumping
// `attempt` is a real network retry for that kind. A stalled load keeps its
// memoized result and is unrecoverable in place, which is why `kind` reaches the
// UI: it decides between offering a retry and offering a reload.
function useStripeInstance(source?: StripeLoadSource): {
  state: StripeInstanceState;
  retry: () => void;
} {
  const [state, setState] = useState<StripeInstanceState>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    getStripe(source).then((result) => {
      if (cancelled) return;
      setState(result);
    });
    return () => {
      cancelled = true;
    };
  }, [attempt, source]);

  // 'retrying' keeps the failure layout on screen with a disabled CTA instead of
  // collapsing to a bare placeholder, so the user keeps the error text, the Back
  // button and their focus while the retry runs.
  const retry = () => {
    setState({ status: 'retrying' });
    setAttempt((n) => n + 1);
  };

  return { state, retry };
}

interface InnerFormProps {
  onSuccess: () => void;
  onError: (message: string) => void;
  isProcessing: boolean;
  setIsProcessing: (processing: boolean) => void;
  submitButtonText: string;
  processingButtonText: string;
  showBackButton: boolean;
  onBack?: () => void;
  backButtonText: string;
  paymentError: string | null;
  submitDisabled?: boolean;
  children?: React.ReactNode;
}

function InnerPaymentForm({
  onSuccess,
  onError,
  isProcessing,
  setIsProcessing,
  submitButtonText,
  processingButtonText,
  showBackButton,
  onBack,
  backButtonText,
  paymentError,
  submitDisabled,
  children,
}: InnerFormProps): JSX.Element {
  const s = useStyles();
  const stripe = useStripe();
  const elements = useElements();
  const isSubmittingRef = useRef(false);
  const [isPaymentFormComplete, setIsPaymentFormComplete] = useState(false);

  const handlePaymentElementChange = (event: StripePaymentElementChangeEvent) => {
    setIsPaymentFormComplete(event.complete);
  };

  // Stripe.js loaded but the Element itself couldn't mount — an integration
  // failure (expired or invalid client secret, key/mode mismatch, Stripe-side
  // outage) rather than anything the user did. Report it: previously this
  // surfaced as a stuck form and generated support tickets, and a silent
  // "handled" error message would remove all pressure to investigate.
  const handleElementLoadError = ({ error }: { error: StripeError }) => {
    // StripeError is a plain object, not an Error, so Sentry serializes the
    // whole thing into extra.__serialized__ — which can carry
    // setup_intent.client_secret and payment_method.billing_details. Report an
    // allowlisted shape instead; nothing in the pipeline redacts payloads.
    const reported = new Error(`Stripe Element load failed: ${error.code ?? error.type}`);
    reported.name = 'StripeElementLoadError';
    Sentry.captureException(reported, {
      tags: { stripe_element_load_failed: 'true' },
      extra: { code: error.code, type: error.type, declineCode: error.decline_code },
    });
    // Deliberately not error.message: unlike the card-decline codes below,
    // integration errors embed object ids and integration instructions, which
    // are unactionable for the user and end up pasted into support tickets.
    onError("We couldn't load the card form. Please refresh and try again.");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    // submitDisabled reached only the button, so a form-level submit (Enter in a field, or
    // any programmatic submit) still ran confirmSetup and captured a card the caller had
    // already decided not to charge (ENG-5571).
    if (submitDisabled) return;
    if (isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    setIsProcessing(true);

    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: window.location.href,
      },
      redirect: 'if_required',
    });

    if (error) {
      switch (error.code) {
        case 'card_declined':
          onError('Your card was declined. Please try a different card.');
          break;
        case 'expired_card':
          onError('Your card has expired. Please use a different card.');
          break;
        case 'incorrect_cvc':
          onError('Incorrect CVC. Please check and try again.');
          break;
        case 'processing_error':
          onError('A processing error occurred. Please try again.');
          break;
        default:
          onError(error.message || 'Payment failed. Please try again.');
      }
      isSubmittingRef.current = false;
      setIsProcessing(false);
    } else if (setupIntent?.status === 'succeeded') {
      onSuccess();
    } else {
      // Handle other statuses (requires_action, etc.)
      onError('Payment could not be completed. Please try again.');
      isSubmittingRef.current = false;
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className={s.formContent}>
        {children}

        {paymentError && (
          <div className={s.paymentError}>
            <p className={s.paymentErrorText}>{paymentError}</p>
          </div>
        )}

        <div className={s.paymentElementWrapper}>
          <div className={s.paymentElementLabel}>Card Details</div>
          <div className={s.paymentElementContainer}>
            <PaymentElement
              options={{ layout: 'tabs' }}
              onLoadError={handleElementLoadError}
              onChange={handlePaymentElementChange}
            />
          </div>
        </div>
      </div>
      <div className={s.footer}>
        {showBackButton && onBack ? (
          <Button variant="text" onClick={onBack} classes={{ root: s.backButton }}>
            {backButtonText}
          </Button>
        ) : (
          <div />
        )}
        <Button
          color="teal"
          size="normal"
          type="submit"
          disabled={
            !stripe || !elements || isProcessing || !isPaymentFormComplete || !!submitDisabled
          }
          aria-busy={isProcessing}
          startIcon={
            isProcessing ? (
              <CircularProgress size={14} thickness={5} sx={{ color: 'inherit' }} />
            ) : undefined
          }
          classes={{ root: cx(s.submitButton, isProcessing && s.submitButtonProcessing) }}
        >
          {isProcessing ? processingButtonText : submitButtonText}
        </Button>
      </div>
    </form>
  );
}

interface StripePaymentMethodFormWithErrorProps extends StripePaymentMethodFormProps {
  paymentError: string | null;
}

export function StripePaymentMethodForm({
  clientSecret,
  onSuccess,
  onError,
  isProcessing,
  setIsProcessing,
  submitButtonText = 'Submit',
  processingButtonText = 'Processing...',
  showBackButton = true,
  onBack,
  backButtonText = 'Back',
  paymentError,
  submitDisabled,
  children,
  failureChildren,
  source,
}: StripePaymentMethodFormWithErrorProps): JSX.Element {
  const s = useStyles();
  const { state: stripeInstance, retry: retryStripeLoad } = useStripeInstance(source);
  const { resolvedTheme } = useThemeMode();
  const appearance = useMemo(() => buildStripeAppearance(resolvedTheme), [resolvedTheme]);

  // A load failure is terminal and independent of the client secret, so it is
  // checked FIRST. The other order left the caller told "failed" while this
  // rendered a placeholder — no error, no retry (Sentry WEBAPP-CCB).
  if (stripeInstance.status === 'failed' || stripeInstance.status === 'retrying') {
    const isRetrying = stripeInstance.status === 'retrying';
    // A stalled load leaves Stripe.js's own promise pending, so calling
    // loadStripe again short-circuits and cannot re-inject the script. Only a
    // reload clears it — offering "try again" there would be a dead button.
    const isRecoverableInPlace = isRetrying || stripeInstance.kind === 'blocked';

    return (
      <StripeFormShell
        showBackButton={showBackButton}
        onBack={onBack}
        backButtonText={backButtonText}
        action={
          isRecoverableInPlace ? (
            <Button
              color="teal"
              size="normal"
              onClick={retryStripeLoad}
              disabled={isRetrying}
              classes={{ root: s.submitButton }}
            >
              {isRetrying ? 'Retrying…' : 'Try again'}
            </Button>
          ) : (
            <Button
              color="teal"
              size="normal"
              onClick={() => window.location.reload()}
              classes={{ root: s.submitButton }}
            >
              Reload page
            </Button>
          )
        }
      >
        {failureChildren ?? children}
        <div className={s.paymentError} role="alert">
          <p className={s.paymentErrorHeading}>Payment couldn&apos;t load</p>
          <p className={s.paymentErrorText}>
            This can happen with ad blockers, browser extensions, or restrictive networks.{' '}
            <span>
              {isRecoverableInPlace
                ? 'Try again, or switch browsers or devices.'
                : 'Reload the page, or switch browsers or devices.'}
            </span>
          </p>
          <p className={s.paymentErrorText}>
            On a work network, ask IT to allow <code>js.stripe.com</code>. If the issue persists,{' '}
            <a className={s.paymentErrorLink} href="mailto:hello@onfrontiers.com">
              contact us
            </a>
            .
          </p>
        </div>
      </StripeFormShell>
    );
  }

  if (!clientSecret || stripeInstance.status === 'loading') {
    return (
      <StripeFormShell
        showBackButton={showBackButton}
        onBack={onBack}
        backButtonText={backButtonText}
        action={<div />}
      >
        {children}
        <div className={s.loading} role="status">
          <CircularProgress />
          <p className={s.loadingText}>Loading payment form…</p>
        </div>
      </StripeFormShell>
    );
  }

  return (
    <Elements
      stripe={stripeInstance.stripe}
      options={{
        clientSecret,
        appearance,
      }}
    >
      <InnerPaymentForm
        onSuccess={onSuccess}
        onError={onError}
        isProcessing={isProcessing}
        setIsProcessing={setIsProcessing}
        submitButtonText={submitButtonText}
        processingButtonText={processingButtonText}
        showBackButton={showBackButton}
        onBack={onBack}
        backButtonText={backButtonText}
        paymentError={paymentError}
        submitDisabled={submitDisabled}
      >
        {children}
      </InnerPaymentForm>
    </Elements>
  );
}

export default StripePaymentMethodForm;
